/**
 * DegreeSourcesPanel — "Verify at source" trust panel on DegreeDetail.
 *
 * Renders the canonical `sources` block populated by
 * `scripts/backfill_source_urls.py`. Every URL is clickable and
 * opens in a new tab so families can audit any programme themselves.
 *
 * Falls back gracefully to `source_provenance.url` when the newer
 * `sources` block isn't yet on the degree (e.g. brand-new inserts
 * that haven't been through the backfill).
 */
import { ShieldCheck, ExternalLink, CalendarCheck2, Landmark, GraduationCap, LineChart } from "lucide-react";

const ICONS = {
  primary: Landmark,
  ucas: GraduationCap,
  university_page: ExternalLink,
  outcomes: LineChart,
};

const ORDER = ["primary", "ucas", "university_page", "outcomes"];

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return String(iso).slice(0, 10);
  }
}

export default function DegreeSourcesPanel({ degree }) {
  const sources = degree?.sources || null;
  const legacyUrl = degree?.source_provenance?.url;
  // Nothing to render — no sources and no legacy URL fallback.
  if (!sources && !legacyUrl) return null;

  const entries = sources
    ? ORDER
        .filter((k) => sources[k]?.url)
        .map((k) => [k, sources[k]])
    : (legacyUrl
        ? [["primary", {
            label: degree.source_provenance?.source || "Official source",
            url: legacyUrl,
            date_checked: degree.source_provenance?.date_checked,
            licence: degree.source_provenance?.licence,
          }]]
        : []);
  if (entries.length === 0) return null;

  return (
    <section
      data-testid="degree-sources-panel"
      className="mt-8 rounded-xl border border-[#4C1D95]/20 bg-[#F5F0FF]/40 p-5"
    >
      <header className="flex items-start gap-3 mb-4">
        <div className="shrink-0 rounded-full bg-[#F5F0FF] p-1.5">
          <ShieldCheck className="h-4 w-4 text-[#4C1D95]" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] font-underground-sc tracking-[0.18em] text-[#4C1D95]">
            Verify at source
          </div>
          <h3 className="mt-1 text-lg font-editorial text-[#002147]">
            Every fact on this page is auditable
          </h3>
          <p className="mt-1 text-[13px] text-stone-700 leading-relaxed max-w-3xl">
            We don't scrape universities' PR copy — the numbers below come from
            official public datasets. Click any link to open the original
            source page in a new tab.
          </p>
        </div>
      </header>

      <ul className="space-y-2" data-testid="degree-sources-list">
        {entries.map(([key, block]) => {
          const Icon = ICONS[key] || ExternalLink;
          const dateStr = fmtDate(block.date_checked || block.scraped_at || block.verified_at);
          return (
            <li key={key} data-testid={`degree-source-${key}`}>
              <a
                href={block.url}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-start gap-3 rounded-lg border border-stone-200 bg-white px-3 py-2.5 hover:border-[#4C1D95]/40 hover:bg-white transition-colors group"
              >
                <div className="mt-0.5 shrink-0 rounded-md bg-[#F5F0FF] p-1.5 group-hover:bg-[#EDE4FF]">
                  <Icon className="h-3.5 w-3.5 text-[#4C1D95]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-sm font-semibold text-stone-900">
                      {block.label}
                    </span>
                    {block.code && (
                      <span className="text-[10px] font-underground-sc tracking-wider rounded-full border border-stone-200 bg-stone-50 text-stone-600 px-1.5 py-0.5">
                        {block.code}
                      </span>
                    )}
                  </div>
                  {block.identifier && (
                    <div className="text-[11px] font-mono text-stone-500 mt-0.5">
                      {block.identifier}
                    </div>
                  )}
                  <div className="text-xs text-stone-500 truncate mt-1">
                    {block.url}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10px] font-underground-sc tracking-wider text-stone-400">
                    {dateStr && (
                      <span className="inline-flex items-center gap-1">
                        <CalendarCheck2 className="h-2.5 w-2.5" /> Checked {dateStr}
                      </span>
                    )}
                    {block.licence && <span>{block.licence}</span>}
                    {block.release && <span>{block.release}</span>}
                  </div>
                </div>
                <ExternalLink className="h-3.5 w-3.5 text-stone-300 group-hover:text-[#4C1D95] mt-1 shrink-0" />
              </a>
            </li>
          );
        })}
      </ul>

      <div className="mt-4 text-[11px] text-stone-500">
        Not sure what these datasets are?{" "}
        <a
          href="/methodology"
          data-testid="degree-sources-methodology-link"
          className="text-[#4C1D95] hover:text-[#3B0764] font-medium hover:underline"
        >
          See our methodology →
        </a>
      </div>
    </section>
  );
}
